import { cn } from '@utils/cn';
import { Badge } from '@atoms/index';

export interface CuisineMultiSelectOption {
  id: string;
  name: string;
}

interface CuisineMultiSelectProps {
  options: CuisineMultiSelectOption[];
  value: string[];
  onChange: (value: string[]) => void;
  label?: string;
  hint?: string;
  disabled?: boolean;
  emptyMessage?: string;
}

export const CuisineMultiSelect = ({
  options,
  value,
  onChange,
  label,
  hint,
  disabled = false,
  emptyMessage = 'Henüz mutfak kategorisi tanımlanmamış.',
}: CuisineMultiSelectProps) => {
  const toggleOption = (optionId: string) => {
    if (disabled) {
      return;
    }
    if (value.includes(optionId)) {
      onChange(value.filter((id) => id !== optionId));
      return;
    }
    onChange([...value, optionId]);
  };

  const selectedOptions = value
    .map((id) => options.find((option) => option.id === id))
    .filter((option): option is CuisineMultiSelectOption => Boolean(option));

  return (
    <div className="space-y-3">
      {label && (
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>
      )}

      {selectedOptions.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selectedOptions.map((option) => (
            <button
              key={option.id}
              type="button"
              disabled={disabled}
              onClick={() => toggleOption(option.id)}
              className={cn(
                'inline-flex items-center gap-1.5 transition-opacity',
                disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:opacity-80',
              )}
              title="Kaldırmak için tıklayın"
            >
              <Badge variant="success" size="sm">
                {option.name}
                {!disabled && <span aria-hidden="true">×</span>}
              </Badge>
            </button>
          ))}
        </div>
      )}

      {options.length === 0 ? (
        <p className="text-sm text-gray-500 rounded-xl border border-dashed border-gray-200 px-4 py-3 bg-gray-50">
          {emptyMessage}
        </p>
      ) : (
        <div
          className={cn(
            'flex flex-wrap gap-2 max-h-44 overflow-y-auto rounded-2xl border border-gray-200 bg-[#F9FAFB] p-3',
            disabled && 'opacity-60 pointer-events-none',
          )}
        >
          {options.map((option) => {
            const isSelected = value.includes(option.id);
            return (
              <button
                key={option.id}
                type="button"
                disabled={disabled}
                onClick={() => toggleOption(option.id)}
                className={cn(
                  'rounded-full px-3.5 py-1.5 text-sm font-medium transition-all duration-200 border',
                  isSelected
                    ? 'bg-primary text-white border-primary shadow-sm'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-primary/40 hover:text-primary',
                )}
              >
                {option.name}
              </button>
            );
          })}
        </div>
      )}

      {hint && <p className="text-xs text-gray-500">{hint}</p>}
    </div>
  );
};

export default CuisineMultiSelect;
